/**
 * Pre-Flight Executor
 *
 * Handles the 'pipeline-pre-flight' node execution.
 * Calls the ModernizeIT API Layer /pre-flight/start endpoint.
 *
 * API Flow:
 * 1. POST /pre-flight/start → returns job_id
 * 2. GET /pre-flight/status/{job_id} → check status
 *
 * This node runs `mvn -DskipTests compile` on generated Java
 * before it reaches full Java Packaging. Produces downloadable
 * artifacts (compile_result.json, compile_failure_sources/, compile_failure_snippets.json)
 * regardless of pass/fail.
 *
 * CRITICAL: On failure, attaches nodeResult to the thrown error so the UI
 * can still show "Download Artifacts" for failed compile jobs.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the pre-flight node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and compile details
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing pre-flight compile check...')

  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Build request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    options: {
      java_version: config.java_version || '17',
      spring_boot_version: config.spring_boot_version || '3.2.0',
      target_profile_id: config.target_profile_id || null,
    },
  }

  onProgress(5, 'Submitting pre-flight job...')

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/pre-flight/start`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Pre-flight failed to start')
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Poll for completion
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Pre-flight complete')
  } else {
    onProgress(15, 'Running Maven compile check...')

    try {
      const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
        maxWaitSeconds: 600, // 10 min — download + scaffold + maven can be slow
        pollInterval: 2000,
        onProgress: (progress, message) => {
          const mappedProgress = 15 + (progress * 0.80)
          onProgress(mappedProgress, message)
        },
        signal,
      })

      Object.assign(jobResponse, completedJob)
    } catch (pollError) {
      // Job failed — fetch output_data for artifact access
      // CRITICAL: attach nodeResult to error so UI can show Download Artifacts
      try {
        const statusResp = await fetch(
          `${apiBaseUrl}/pre-flight/status/${jobId}`,
          { headers: getAuthHeaders() }
        )
        if (statusResp.ok) {
          const failedData = await statusResp.json()
          const partialResult = formatResult(jobId, failedData)
          partialResult.failed = true

          const err = new Error(
            failedData.error || pollError.message || 'Pre-Flight compile check failed'
          )
          err.nodeResult = partialResult
          throw err
        }
      } catch (fetchErr) {
        // If status fetch itself throws with nodeResult, propagate it
        if (fetchErr.nodeResult) throw fetchErr
      }
      // Fallback: re-throw original poll error
      throw pollError
    }
  }

  onProgress(100, 'Pre-flight compile check complete')

  return formatResult(jobId, jobResponse)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data) {
  return {
    // Job tracking
    jobs: {
      pre_flight_job_id: jobId,
    },

    // Status
    success: data.success !== false && data.status !== 'failed',
    status: data.status || 'completed',

    // Compile summary
    compile_passed: data.compile_passed ?? null,
    errors: data.errors ?? 0,
    java_files_checked: data.java_files_checked ?? 0,

    // Error info
    error: data.error || null,
  }
}

export default {
  execute,
}
